import { createAction, createAsyncThunk, type Dispatch } from '@reduxjs/toolkit';

import type {
  AcaoDeEmissao,
  ChaveDoCliente,
  ChaveEmitida,
  DadosDaChaveNova,
  ErroDasChaves,
  ResultadoDaEmissao,
  RotacaoDeChave,
} from '../types/chavesDeApiTypes';
import { chavesDeApiService, FalhaDasChaves } from './services';

interface Config {
  rejectValue: ErroDasChaves;
}

/**
 * Falha de rede e "Sessão expirada" do requestBuilder chegam como `Error` comum: status 0 separa
 * "nem chegou ao core" de uma recusa do core.
 */
const paraErroDasChaves = (erro: unknown): ErroDasChaves =>
  erro instanceof FalhaDasChaves
    ? erro.erro
    : {
        status: 0,
        codigo: null,
        mensagem: 'Não foi possível falar com o servidor. Verifique a conexão e tente novamente.',
      };

export const listarChavesDeApi = createAsyncThunk<
  { escritorioId: string; chaves: ChaveDoCliente[] },
  string,
  Config
>('chavesDeApi/listar', async (escritorioId, { rejectWithValue }) => {
  try {
    return { escritorioId, chaves: await chavesDeApiService.listar(escritorioId) };
  } catch (erro) {
    return rejectWithValue(paraErroDasChaves(erro));
  }
});

export const emissaoIniciada = createAction<AcaoDeEmissao>('chavesDeApi/emissaoIniciada');
export const emissaoConcluida = createAction('chavesDeApi/emissaoConcluida');
export const emissaoRecusada = createAction<ErroDasChaves>('chavesDeApi/emissaoRecusada');

/**
 * Criar e rotacionar não usam `createAsyncThunk`: ele põe o retorno no `payload` do `fulfilled`, e
 * o Redux DevTools (ligado por padrão no `configureStore`) guarda toda ação despachada, mesmo a que
 * o reducer ignora. Este thunk entrega a `ChaveEmitida` só a quem o despachou, pelo retorno do
 * `dispatch`; as ações que ele despacha levam a ação e o erro, nunca o texto (R4.2).
 */
const emitirChave =
  (acao: AcaoDeEmissao, chamar: () => Promise<ChaveEmitida>) =>
  async (dispatch: Dispatch): Promise<ResultadoDaEmissao> => {
    dispatch(emissaoIniciada(acao));
    try {
      const emitida = await chamar();
      dispatch(emissaoConcluida());
      return { emitida, erro: null };
    } catch (erro) {
      const falha = paraErroDasChaves(erro);
      dispatch(emissaoRecusada(falha));
      return { emitida: null, erro: falha };
    }
  };

export const criarChaveDeApi = ({
  escritorioId,
  dados,
}: {
  escritorioId: string;
  dados: DadosDaChaveNova;
}) => emitirChave('criar', () => chavesDeApiService.criar(escritorioId, dados));

export const rotacionarChaveDeApi = ({
  escritorioId,
  rotacao,
}: {
  escritorioId: string;
  rotacao: RotacaoDeChave;
}) => emitirChave('rotacionar', () => chavesDeApiService.rotacionar(escritorioId, rotacao));

export const revogarChaveDeApi = createAsyncThunk<
  void,
  { escritorioId: string; chaveId: string },
  Config
>('chavesDeApi/revogar', async ({ escritorioId, chaveId }, { rejectWithValue }) => {
  try {
    await chavesDeApiService.revogar(escritorioId, chaveId);
  } catch (erro) {
    return rejectWithValue(paraErroDasChaves(erro));
  }
});
